import React, { useEffect, useMemo, useState } from "react";

import AppLayout from "components/ui/AppLayout";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import MetricCards from "./components/MetricCards";
import CustomerJourneyFunnel from "./components/CustomerJourneyFunnel";
import ActivityFeed from "./components/ActivityFeed";
import SatisfactionTrends from "./components/SatisfactionTrends";
import SegmentationMatrix from "./components/SegmentationMatrix";
import RetentionCohort from "./components/RetentionCohort";
import ChurnPrediction from "./components/ChurnPrediction";
import AcquisitionChannels from "./components/AcquisitionChannels";
import DataUnavailable from "components/ui/DataUnavailable";
import { getCurrentUser } from "api/client";
import { reportsApi, formatLastDataUtc } from "api/reports";

const ACCORDION_SECTIONS = [
  { id: "funnel", label: "Customer Journey Funnel", icon: "Filter" },
  { id: "segmentation", label: "Segmentation Matrix", icon: "Grid3X3" },
  { id: "cohort", label: "Retention Cohort", icon: "Calendar" },
  { id: "churn", label: "Churn Prediction", icon: "AlertTriangle" },
  { id: "channels", label: "Acquisition Channels", icon: "Share2" },
  { id: "satisfaction", label: "Satisfaction Trends", icon: "TrendingUp" },
];

export default function CustomerAnalytics() {
  const currentUser = getCurrentUser();
  const [activeMetricCard, setActiveMetricCard] = useState(null);
  const [selectedChannel, setSelectedChannel] = useState("all");
  const [openAccordions, setOpenAccordions] = useState(["funnel", "segmentation"]);
  const [widgets, setWidgets] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);
  const [loadError, setLoadError] = useState("");

  const channelOptions = useMemo(() => {
    const names = Object.keys(widgets?.acquisitionChannels || {});
    return [{ value: "all", label: "All Channels" }, ...names.map((name) => ({ value: name, label: name }))];
  }, [widgets?.acquisitionChannels]);

  useEffect(() => {
    const loadWidgets = async () => {
      try {
        const widgetsData = await reportsApi.getCustomers();
        setWidgets(widgetsData);
        setUnavailable(false);
        setLoadError("");
      } catch (error) {
        if (error?.code === "NOT_AVAILABLE") {
          setUnavailable(true);
        } else {
          setLoadError(error?.message || "Unable to load live customer analytics data.");
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadWidgets();
  }, []);

  const retry = () => {
    setIsLoading(true);
    setUnavailable(false);
    setLoadError("");
    reportsApi.getCustomers()
      .then((widgetsData) => {
        setWidgets(widgetsData);
        setIsLoading(false);
      })
      .catch((error) => {
        if (error?.code === "NOT_AVAILABLE") setUnavailable(true);
        else setLoadError(error?.message || "Unable to load live customer analytics data.");
        setIsLoading(false);
      });
  };

  const toggleAccordion = (id) => {
    setOpenAccordions((prev) =>
      prev?.includes(id) ? prev?.filter((x) => x !== id) : [...prev, id]
    );
  };

  const channelsFromSummary = widgets?.acquisitionChannels
    ? Object.entries(widgets.acquisitionChannels).map(([name, customers], index, arr) => {
        const total = arr.reduce((acc, [, count]) => acc + count, 0);
        const palette = ["#1E40AF", "#7C3AED", "#059669", "#D97706", "#DB2777", "#6B7280"];
        return {
          name,
          customers,
          value: total > 0 ? Math.round((customers / total) * 100) : 0,
          color: palette[index] || "#6B7280",
        };
      })
    : null;

  const handleExport = (dataset) => {
    reportsApi.exportDataset(dataset).catch((error) => {
      setLoadError(error?.message || "Export failed.");
    });
  };

  return (
    <AppLayout>
      <div className="min-h-screen" style={{ background: "var(--color-background)" }}>
        <main className="px-4 md:px-6 lg:px-8 py-5 lg:py-6 max-w-screen-2xl mx-auto">
          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
            <div>
              <h1
                className="font-heading font-bold text-2xl md:text-3xl"
                style={{ color: "var(--color-foreground)", letterSpacing: "-0.02em" }}
              >
                Customer Analytics
              </h1>
              <p className="font-caption text-sm mt-0.5" style={{ color: "var(--color-text-secondary)" }}>
                Behavioral analysis, retention strategies &amp; lifecycle insights
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Export endpoint is Owner/Manager only (Sprint 15 QA). */}
              {currentUser?.role === "Owner" || currentUser?.role === "Manager" ? (
              <Button variant="outline" iconName="Download" iconPosition="left" iconSize={14} size="sm"
                onClick={() => handleExport("contacts")}>
                Export Contacts
              </Button>
              ) : null}
            </div>
          </div>

          {/* Filters row */}
          <div className="mb-5">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="font-caption text-xs font-medium" style={{ color: "var(--color-text-secondary)" }}>
                  Channel:
                </span>
                {channelOptions?.slice(0, 5)?.map((c) => (
                  <button
                    key={c?.value}
                    onClick={() => setSelectedChannel(c?.value)}
                    className="px-2.5 py-1 rounded-full font-caption text-xs font-medium transition-all duration-150"
                    style={{
                      background: selectedChannel === c?.value ? "#7C3AED" : "var(--color-muted)",
                      color: selectedChannel === c?.value ? "#fff" : "var(--color-text-secondary)",
                      minHeight: 28,
                    }}
                  >
                    {c?.label}
                  </button>
                ))}
              </div>
            </div>

            <Button variant="outline" size="sm" iconName="RefreshCw" iconPosition="left" iconSize={14} onClick={retry} disabled={isLoading}>
              {isLoading ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>

          {/* Live data indicator */}
          <div
            className="flex items-center gap-2 px-3 py-2 rounded-lg mb-5"
            style={{ background: "var(--color-muted)", border: "1px solid var(--color-border)" }}
          >
            <div className="w-2 h-2 rounded-full bg-emerald-500" />
            <span className="font-caption text-xs" style={{ color: "var(--color-text-secondary)" }}>
              Live data — reads straight from the CRM. As of{" "}
              <strong style={{ color: "var(--color-foreground)" }}>{formatLastDataUtc(widgets?.lastDataAtUtc)}</strong>
            </span>
          </div>

          {/* Metric Cards */}
          {!isLoading && unavailable && (
            <DataUnavailable feature="Customer Analytics" onRetry={retry} />
          )}
          {!unavailable && (
          <>
          <section className="mb-5">
            {isLoading && (
              <div className="rounded-lg border px-4 py-5" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
                <p className="font-caption text-sm" style={{ color: "var(--color-text-secondary)" }}>Loading live customer analytics data...</p>
              </div>
            )}
            {!isLoading && loadError && (
              <div className="rounded-lg border px-4 py-5" style={{ borderColor: "var(--color-error)", background: "var(--color-card)" }}>
                <p className="font-caption text-sm" style={{ color: "var(--color-error)" }}>{loadError}</p>
              </div>
            )}
            <MetricCards
              onCardClick={setActiveMetricCard}
              activeCard={activeMetricCard}
              metrics={widgets?.metricCards}
            />
          </section>

          {/* DESKTOP LAYOUT */}
          <div className="hidden lg:block">
            <div className="grid grid-cols-3 gap-5 mb-5">
              <div className="col-span-2" style={{ minHeight: 380 }}>
                <CustomerJourneyFunnel
                  funnelData={widgets?.journeyFunnel}
                  touchpointsMap={widgets?.touchpoints}
                />
              </div>
              <div className="col-span-1 flex flex-col gap-5">
                <div style={{ flex: "1 1 0", minHeight: 0 }}>
                  <ActivityFeed activities={widgets?.activityFeed} />
                </div>
              </div>
            </div>
            <div className="mb-5"><SatisfactionTrends data={widgets?.satisfactionTrends} /></div>
            <div className="mb-5"><SegmentationMatrix segments={widgets?.segmentationMatrix} /></div>
            <div className="grid grid-cols-2 gap-5 mb-5">
              <RetentionCohort cohortData={widgets?.retentionCohort} />
              <ChurnPrediction churnData={widgets?.churnSegments} atRiskCustomers={widgets?.atRiskAccounts} />
            </div>
            <div className="mb-5"><AcquisitionChannels channels={channelsFromSummary} /></div>
          </div>

          {/* TABLET LAYOUT */}
          <div className="hidden md:block lg:hidden">
            <div className="grid grid-cols-2 gap-4 mb-4">
              <CustomerJourneyFunnel
                funnelData={widgets?.journeyFunnel}
                touchpointsMap={widgets?.touchpoints}
              />
              <ActivityFeed activities={widgets?.activityFeed} />
            </div>
            <div className="mb-4"><SatisfactionTrends data={widgets?.satisfactionTrends} /></div>
            <div className="mb-4"><SegmentationMatrix segments={widgets?.segmentationMatrix} /></div>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <RetentionCohort cohortData={widgets?.retentionCohort} />
              <ChurnPrediction churnData={widgets?.churnSegments} atRiskCustomers={widgets?.atRiskAccounts} />
            </div>
            <div className="mb-4"><AcquisitionChannels channels={channelsFromSummary} /></div>
          </div>

          {/* MOBILE LAYOUT */}
          <div className="md:hidden">
            <div className="mb-4"><ActivityFeed activities={widgets?.activityFeed} /></div>
            {ACCORDION_SECTIONS?.map((section) => {
              const isOpen = openAccordions?.includes(section?.id);
              return (
                <div
                  key={section?.id}
                  className="mb-3 rounded-xl border overflow-hidden"
                  style={{ background: "var(--color-card)", borderColor: "var(--color-border)" }}
                >
                  <button
                    onClick={() => toggleAccordion(section?.id)}
                    className="w-full flex items-center justify-between px-4 py-3 transition-all duration-150"
                    style={{ background: isOpen ? "var(--color-muted)" : "var(--color-card)", minHeight: 48 }}
                    aria-expanded={isOpen}
                  >
                    <div className="flex items-center gap-2">
                      <Icon name={section?.icon} size={16} color={isOpen ? "var(--color-primary)" : "var(--color-text-secondary)"} />
                      <span className="font-caption text-sm font-semibold" style={{ color: isOpen ? "var(--color-primary)" : "var(--color-foreground)" }}>
                        {section?.label}
                      </span>
                    </div>
                    <Icon name={isOpen ? "ChevronUp" : "ChevronDown"} size={16} color="var(--color-text-secondary)" />
                  </button>
                  {isOpen && (
                    <div className="p-3">
                      {section?.id === "funnel" && (
                        <CustomerJourneyFunnel
                          funnelData={widgets?.journeyFunnel}
                          touchpointsMap={widgets?.touchpoints}
                        />
                      )}
                      {section?.id === "segmentation" && <SegmentationMatrix segments={widgets?.segmentationMatrix} />}
                      {section?.id === "cohort" && <RetentionCohort cohortData={widgets?.retentionCohort} />}
                      {section?.id === "churn" && <ChurnPrediction churnData={widgets?.churnSegments} atRiskCustomers={widgets?.atRiskAccounts} />}
                      {section?.id === "channels" && <AcquisitionChannels channels={channelsFromSummary} />}
                      {section?.id === "satisfaction" && <SatisfactionTrends data={widgets?.satisfactionTrends} />}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          </>
            )}

          {/* Footer */}
          <footer className="mt-8 pt-4 border-t text-center" style={{ borderColor: "var(--color-border)" }}>
            <p className="font-caption text-xs" style={{ color: "var(--color-text-secondary)" }}>
              &copy; {new Date()?.getFullYear()} CRMAnalytics. All rights reserved. Live data — as of{" "}
              {formatLastDataUtc(widgets?.lastDataAtUtc)}.
            </p>
          </footer>
        </main>
      </div>
    </AppLayout>
  );
}